import React, { useState } from "react";
import { Lock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const TOPICS = [
  "Men's sexual health",
  "Women's sexual health",
  "Couple / relationship",
  "Medication or medical illness related",
  "Something else",
];

export default function AnonymousEnquiry({ t = (s) => s }) {
  const [form, setForm] = useState({ topic: TOPICS[0], question: "", age_gender: "", contact: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.question.trim().length < 10) {
      toast.error(t("Please write your question in a little more detail."));
      return;
    }
    setSending(true);
    try {
      await api.post("/enquiries", form);
      setSent(true);
      setForm({ topic: TOPICS[0], question: "", age_gender: "", contact: "" });
      toast.success(t("Your question has reached the doctors — privately."));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="anonymous-enquiry" className="bg-[#FDFBF7] rounded-3xl border border-[#A39171]/20 p-8 md:p-12 shadow-[0_20px_60px_rgba(163,145,113,0.10)]">
      <span className="w-12 h-12 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center mb-6">
        <Lock className="w-5 h-5" />
      </span>
      <p className="eyebrow text-[#A39171] mb-4">{t("Ask Anonymously")}</p>
      <h2 className="font-display text-3xl md:text-4xl text-[#2D2623] tracking-tight leading-tight">
        {t("Not ready to walk in yet?")} <span className="italic text-[#E07A5F]">{t("Just ask.")}</span>
      </h2>
      <p className="mt-5 text-[#5C524E] leading-relaxed max-w-2xl">
        {t("Write your question without giving your name. It goes straight to Dr. Kamlesh and Dr. Dharmin — nobody else sees it. Leave a phone or email only if you'd like a private reply.")}
      </p>

      {sent ? (
        <div data-testid="enquiry-success" className="mt-8 bg-[#F4EFE6] border border-[#E07A5F]/30 rounded-2xl p-7">
          <p className="font-display text-2xl text-[#2D2623]">{t("Received — thank you for trusting us.")}</p>
          <p className="text-[#5C524E] mt-2">
            {t("If you left a contact detail, a doctor will reply personally and discreetly. You can ask another question any time.")}
          </p>
          <Button
            data-testid="enquiry-ask-another"
            onClick={() => setSent(false)}
            className="mt-6 rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-7 h-11"
          >
            {t("Ask another question")}
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-5 max-w-2xl">
          <div>
            <Label className="text-sm text-[#5C524E] mb-2 block">{t("What is it about?")}</Label>
            <select
              data-testid="enquiry-topic"
              value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              className="w-full bg-white border border-[#A39171]/30 rounded-xl h-12 px-4 text-[#2D2623]"
            >
              {TOPICS.map((tp) => (
                <option key={tp} value={tp}>{t(tp)}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-sm text-[#5C524E] mb-2 block">{t("Your question")} *</Label>
            <Textarea
              data-testid="enquiry-question"
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              placeholder={t("Write freely. No detail is too embarrassing — we have heard it before.")}
              maxLength={4000}
              className="bg-white border-[#A39171]/30 rounded-xl min-h-[140px]"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">{t("Age & gender (optional)")}</Label>
              <Input
                data-testid="enquiry-age-gender"
                value={form.age_gender}
                onChange={(e) => setForm({ ...form, age_gender: e.target.value })}
                placeholder={t("e.g. 32, male")}
                className="bg-white border-[#A39171]/30 rounded-xl h-12"
              />
            </div>
            <div>
              <Label className="text-sm text-[#5C524E] mb-2 block">{t("Phone or email for a private reply (optional)")}</Label>
              <Input
                data-testid="enquiry-contact"
                value={form.contact}
                onChange={(e) => setForm({ ...form, contact: e.target.value })}
                placeholder={t("Leave blank to stay fully anonymous")}
                className="bg-white border-[#A39171]/30 rounded-xl h-12"
              />
            </div>
          </div>
          <Button
            data-testid="enquiry-submit"
            type="submit"
            disabled={sending}
            className="rounded-full bg-[#E07A5F] hover:bg-[#2D2623] text-white px-8 h-12 font-medium"
          >
            {sending ? t("Sending…") : t("Send Privately")} <Send className="ml-2 w-4 h-4" />
          </Button>
          <p className="text-xs text-[#A39171]">
            {t("We never store your IP, never send marketing messages, and never share enquiries with anyone outside the clinic.")}
          </p>
        </form>
      )}
    </div>
  );
}
